import { InputBase, InputProps, Stack, styled, Typography } from "@mui/material";
import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { useRifm } from "rifm";

const StyledInput = styled(InputBase)`
  white-space: pre-wrap;
  line-height: 24px;
  padding: 0 0 0 5px;

  input {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

interface Props extends InputProps {
  isEditable?: boolean;
  maxLength?: number;
  maskOptions?: MaskOptions;
}

interface MaskOptions {
  accept?: RegExp;
  format: (str: string) => string;
}

const BorderlessTextField = React.forwardRef((props: Props, ref: React.Ref<unknown>) => {
  const { value, sx, placeholder, onBlur, onFocus, isEditable, type, maskOptions, maxLength, ...otherProps } = props;
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [internalValue, setInternalValue] = useState<string>(String(value));
  const inputRef = useRef<HTMLInputElement>(null);
  const rifm = useRifm({
    value: internalValue,
    onChange: setInternalValue,
    accept: props.maskOptions?.accept,
    format: props.maskOptions?.format ?? ((str) => str),
  });

  useImperativeHandle(
    ref,
    () => {
      return {
        focus() {
          setIsFocused(true);
          inputRef.current?.focus();
        },
      };
    },
    []
  );

  useEffect(() => {
    setInternalValue(String(value));
  }, [value]);

  if (isEditable) {
    return (
      <Stack sx={{ width: "100%" }}>
        <StyledInput
          inputRef={inputRef}
          {...otherProps}
          placeholder={placeholder}
          value={props.maskOptions ? rifm.value : internalValue}
          onChange={(e) => (props.maskOptions ? rifm.onChange(e) : setInternalValue(e.target.value))}
          onFocus={(e) => {
            onFocus?.(e);
            setIsFocused(true);
          }}
          onBlur={(e) => {
            onBlur?.(e);
            setIsFocused(false);
          }}
          fullWidth
          inputProps={{ maxLength: props.maxLength }}
          sx={{
            ...sx,
          }}
        />

        {props.maxLength && isFocused && internalValue.length > props.maxLength * 0.5 ? (
          <Typography
            variant="caption"
            color={internalValue.length >= props.maxLength ? "orange" : "text.secondary"}
            sx={{ pl: 0.25 }}
          >
            {internalValue.length}/{props.maxLength}
          </Typography>
        ) : undefined}
      </Stack>
    );
  } else {
    return <Typography sx={{ minHeight: "34px", p: 0.25 }}>{internalValue}</Typography>;
  }
});

export { BorderlessTextField };
